import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BaseComponent } from '../../base/base.component';
import { IconButtonComponent } from '../buttons/icon-button.component';

@Component({
  selector: 'app-toast',
  imports: [CommonModule, IconButtonComponent],
  template: `
    <div class="tw-flex tw-items-center fade-in">
      <div class="toast-container tw-flex tw-px-2" [ngClass]="toastClass">
        @if (toastIcon) {
          <span
            class="left-border tw-justify-center"
            role="img"
            [attr.aria-label]="toastAriaLabel"
          >
            <i
              aria-hidden="true"
              class="tw-mr-1 icon"
              [class.tw-animate-pulse]="type === 'Error'"
              [ngClass]="toastIcon"
            ></i>
          </span>
        }
        <div class="toast-text tw-grow">
          <span
            [attr.role]="type === 'Error' ? 'alert' : null"
            [attr.aria-live]="type === 'Error' ? 'assertive' : 'polite'"
            [attr.aria-atomic]="'true'"
          >
            <ng-content></ng-content>
          </span>
        </div>
        @if (dismiss) {
          <div class="tw-grow tw-items-center tw-flex tw-justify-end">
            <app-icon-button
              icon="fa-light fa-xmark"
              (click)="dismissed.emit()"
            />
          </div>
        }
      </div>
    </div>
  `,
  styles: [
    `
      .icon {
        font-size: 20px;
      }
      .toast-container {
        border-radius: 4px;
        color: var(--sed-warm-white-color);
        font-weight: bold;
        letter-spacing: 0.02rem;
      }
      .toast-text {
        padding: 10px 10px 10px 0px;
      }

      .left-border {
        border-radius: 4px 0px 0px 4px;
        min-width: 40px;
        display: flex;
        align-items: center;
      }

      .toast-success {
        background: var(--sed-status-green-color);
      }

      .toast-info {
        color: var(--sed-engaged-black-color);
        background-color: var(--sed-purple-40-color);
      }

      .toast-error {
        background: var(--sed-status-red-color);
      }

      .fade-in {
        animation: 500ms fade-in-kf;
      }

      @keyframes fade-in-kf {
        0% {
          opacity: 0;
        }
        100% {
          opacity: 1;
        }
      }
    `,
  ],
})
export class ToastComponent extends BaseComponent implements OnInit {
  @Input()
  type: ToastType;

  @Input()
  icon: string;

  @Input()
  dismiss = false;

  @Output()
  dismissed = new EventEmitter();

  @Input()
  dismissSeconds: number;

  get toastClass(): string {
    switch (this.type) {
      case 'Error':
        return 'toast-error';
      case 'Success':
        return 'toast-success';
      case 'Info':
        return 'toast-info';
      default:
        return 'toast-info';
    }
  }

  get toastIcon(): string {
    if (this.icon) {
      return this.icon;
    }

    switch (this.type) {
      case 'Error':
        return 'fa-circle-xmark fa-light';
      case 'Success':
        return 'fa-thumbs-up fa-light';
      case 'Info':
        return 'fa-circle-info fa-light';
      default:
        return 'fa-circle-info fa-light';
    }
  }

  get toastAriaLabel(): string {
    switch (this.type) {
      case 'Error':
        return 'Error';
      case 'Success':
        return 'Success';
      case 'Info':
        return 'Information';
      default:
        return 'Information';
    }
  }

  ngOnInit(): void {
    if (this.dismissSeconds) {
      this.dispose.timeouts = setTimeout(() => {
        this.dismissed.emit();
      }, this.dismissSeconds * 1000);
    }
  }

  onDismiss() {
    this.dismissed.emit();
  }
}

export type ToastType = 'Info' | 'Success' | 'Error';
